// DataCharts.jsx
import React, { useState, useMemo, useRef, useEffect } from "react";
import Select from "react-select";

// DevExtreme Chart
import {
  Chart,
  ArgumentAxis,
  ValueAxis,
  LineSeries,
  BarSeries,
  StackedBarSeries,
  ScatterSeries,
  Title,
  Tooltip,
  Legend,
} from "@devexpress/dx-react-chart-material-ui";

import { ArgumentScale, Animation } from "@devexpress/dx-react-chart";

import { ErrorBoundary } from "./ErrorBoundary";

// Stili
let chartContainer = {
  backgroundColor: "#f8f9fa",
  padding: "1rem",
  borderRadius: "0.5rem",
  marginBottom: "1rem",
};

// ======================================================================
// UTILITY: Aggrega i dati per prodotto
// ======================================================================
const aggregateDataByProduct = (data) => {
  const productMap = new Map();

  data.forEach((item) => {
    const key = item.productName;

    if (!productMap.has(key)) {
      productMap.set(key, {
        sku: item.sku,
        productName: item.productName,
        periodi: { ...item.periodi },
        totalDiff: item.diff,
        totalPercentage: 0,
        regions: [item.regione],
        provinces: [item.prov],
      });
    } else {
      const existing = productMap.get(key);

      // Somma i valori per ogni periodo
      Object.keys(item.periodi).forEach((periodo) => {
        existing.periodi[periodo] = (existing.periodi[periodo] || 0) + item.periodi[periodo];
      });

      existing.totalDiff += item.diff;
      existing.regions.push(item.regione);
      existing.provinces.push(item.prov);
    }
  });

  // Calcola le percentuali aggregate
  return Array.from(productMap.values()).map((item) => {
    const periodi = Object.keys(item.periodi).sort();
    if (periodi.length >= 2) {
      const firstValue = item.periodi[periodi[0]] || 0;
      const lastValue = item.periodi[periodi[periodi.length - 1]] || 0;

      if (firstValue === 0) {
        item.percentage = lastValue > 0 ? 100 : 0;
      } else {
        item.percentage = ((lastValue - firstValue) / firstValue) * 100;
      }
    } else {
      item.percentage = 0;
    }

    return {
      ...item,
      percentage: Math.round(item.percentage * 100) / 100, // Arrotonda a 2 decimali
    };
  });
};

// ======================================================================
// COMPONENTE PRINCIPALE
// ======================================================================
export const DataCharts = ({ data, periodColumns, showLines = true, isMobile = false }) => {
  const _height = showLines ? "220" : "300";

  chartContainer = { ...chartContainer, height: _height + 50 };

  // Aggrega i dati per prodotto (SEMPRE prima del return condizionale)
  const aggregatedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return aggregateDataByProduct(data);
  }, [data]);

  // Se non ci sono dati, non mostrare nulla
  if (!data || data.length === 0) return null;

  return (
    <div className="row">
      {/* Grafico 1: Andamento prodotto */}

      {showLines && (
        <div className="col-md-12">
          <div style={chartContainer}>
            <ErrorBoundary>
              <ProductTrendChart data={aggregatedData} height={_height} periodColumns={periodColumns} />
            </ErrorBoundary>
          </div>
        </div>
      )}

      {/* Grafico 2: Top 5 variations % */}
      {!showLines && (
        <div className={`col-md-${showLines ? "6" : "12"}`}>
          <div style={chartContainer}>
            <TopGrowthChart data={aggregatedData} height={_height} isMobile={isMobile} />
          </div>
        </div>
      )}
    </div>
  );
};

// ======================================================================
// GRAFICO 1: Andamento prodotto (Line Chart)
// ======================================================================
const ProductTrendChart = ({ data, periodColumns, height }) => {
  const isMounted = useRef(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // const productOptions = useMemo(() => {
  //   return data
  //     .map((d) => ({ value: d.productName, label: d.productName }))
  //     .sort((a, b) => a.label.localeCompare(b.label));
  // }, [data]);

  // Formatta il periodo per la visualizzazione
  const formatPeriod = (periodo) => {
    // Se è un intervallo (es. "Gen 2024 - Giu 2024")
    if (periodo.includes(" - ")) {
      return periodo; // Mostra il testo così com'è
    }

    // Se è una data ISO (es. "2024-01-01")
    if (periodo.includes("-") && /^\d{4}-\d{2}/.test(periodo)) {
      const date = new Date(periodo);
      if (isNaN(date.getTime())) return periodo; // Se non è una data valida
      return date.toLocaleDateString("it-IT", { month: "short", year: "numeric" });
    }

    // Altrimenti, restituisce il valore originale
    return periodo;
  };

  const chartData = useMemo(() => {
    if (selectedProduct) {
      // Modalità prodotto singolo
      const item = data.find((d) => d.productName === selectedProduct);
      if (!item) return [];

      return periodColumns.map((periodo) => ({
        periodo: formatPeriod(periodo),
        Vendite: item.periodi?.[periodo] || 0,
      }));
    } else {
      // Modalità tutti i prodotti
      return periodColumns.map((periodo) => {
        const periodData = { periodo: formatPeriod(periodo) };
        data.forEach((item) => {
          // Usa una chiave sicura per il prodotto
          const safeKey = `product_${data.indexOf(item)}`;
          periodData[safeKey] = item.periodi?.[periodo] || 0;
        });
        return periodData;
      });
    }
  }, [selectedProduct, data, periodColumns]);

  const renderSeries = useMemo(() => {
    if (selectedProduct) {
      // Una sola serie per il prodotto selezionato
      return <LineSeries valueField="Vendite" argumentField="periodo" color="#69aa46" />;
    } else {
      // Serie multiple per tutti i prodotti
      const colors = [
        "#69aa46",
        "#42a5f5",
        "#ff7043",
        "#ab47bc",
        "#66bb6a",
        "#ffa726",
        "#ef5350",
        "#26c6da",
        "#d4e157",
        "#8d6e63",
      ];

      return data.map((item, index) => {
        const safeKey = `product_${index}`;
        return (
          <LineSeries
            key={`series_${index}_${item.productName}`}
            name={item.productName}
            valueField={safeKey}
            argumentField="periodo"
            color={colors[index % colors.length]}
          />
        );
      });
    }
  }, [selectedProduct, data]);

  // Chiave unica per forzare il re-render del grafico
  const chartKey = useMemo(() => {
    return `chart-${selectedProduct || "all"}-${data.length}-${Date.now()}`;
  }, [selectedProduct, data]);

  if (!isMounted.current) {
    return <div>Caricamento...</div>;
  }

  return (
    <div>
      {/* <h6>Andamento prodotto</h6> */}
      {/* <Select
        options={productOptions}
        value={selectedProduct ? { value: selectedProduct, label: selectedProduct } : null}
        onChange={(opt) => setSelectedProduct(opt?.value || null)}
        placeholder="Seleziona prodotto (vuoto = tutti)..."
        styles={{ container: (base) => ({ ...base, fontSize: "0.875rem" }) }}
        menuPortalTarget={document.body}
        isClearable
      /> */}
      {chartData.length > 0 ? (
        <Chart data={chartData} key={chartKey} height={height}>
          <ArgumentScale />
          {/* <ArgumentAxis /> */}
          <ValueAxis />
          {renderSeries}
          {/* <Title text={selectedProduct ? `Vendite ${selectedProduct} nel tempo` : "Andamento vendite per periodo"} /> */}
          {isMounted.current && <Tooltip />}
          {/* {!selectedProduct && <Legend />} */}
          {/* {isMounted.current && <Animation/> } */}
        </Chart>
      ) : (
        <div className="text-muted text-center mt-4">Nessun dato disponibile</div>
      )}
    </div>
  );
};

// ======================================================================
// GRAFICO 2: Top 3 per  % (Bar Chart)
// ======================================================================
const TopGrowthChart = ({ data, height, isMobile = false }) => {
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 3)
      .map((item, index) => {
        var _labelProduct = item.productName.length > 30 ? item.productName.substring(0, 30) + "..." : item.productName;
        //  Se sono su mobile splitto con gli " " e prendo le prime 2 lettere di ogni item.
        if (isMobile) {
          _labelProduct = item.productName
            .split(" ")
            // .map((word) => word.substring(0, 3))
            .join(" ");
        }
        return {
          product: _labelProduct,
          // productName: item.productname,
          variations: Number(item.percentage) || 0,
          id: `bar_${index}`, // ID unico per ogni barra
        };
      });
  }, [data]);

  // Chiave unica per il re-render
  const chartKey = useMemo(() => {
    if (!isMounted.current) return "unmounted";
    return `bar-chart-${chartData.length}-${JSON.stringify(chartData.slice(0, 3))}`;
  }, [chartData]);

  if (!isMounted.current) {
    return <div>Caricamento...</div>;
  }

  return (
    <div>
      {/* <h6>Top 3 Variazioni %</h6> */}
      <Chart data={chartData} key={chartKey} height={height}>
        <ArgumentAxis
          labelComponent={({ text, ...restProps }) => {
            if (!isMounted.current) return null;

            let words = text.split(" ");

            if (isMobile) {
              return (
                <text {...restProps} dominantBaseline="middle" fontSize={10} fill="#666">
                  {words.map((word, index) => (
                    <tspan key={index} x={restProps.x} dy={index === 0 ? 0 : "1.2em"}>
                      {word}
                    </tspan>
                  ))}
                </text>
              );
            }
            return (
              <text {...restProps} dominantBaseline="middle" fontSize={10} fill="#666">
                {text}
              </text>
            );
          }}
        />
        <ValueAxis
          showLabels={true}
          showLine={true}
          showGrid={true}
          labelComponent={({ text, ...restProps }) => {
            if (!isMounted.current) return null;
            return (
              <text {...restProps} fontSize="11" fill="#666">
                {text}%
              </text>
            );
          }}
        />
        <BarSeries valueField="variations" argumentField="product" color="#69aa46" />
        <Title text="Top 3 Crescita" />
        <Tooltip
          contentComponent={({ targetItem }) => {
            if (!isMounted.current) return null;
            const item = targetItem?.data;
            if (!item) return null;

            return (
              <div className="p-2">
                <strong>{item.productName}</strong>: {item.variations > 0 ? "+" : ""}
                {Number(item.variations).toFixed(1)}%
              </div>
            );
          }}
        />
        {/* {isMounted.current && <Animation/> } */}
      </Chart>
    </div>
  );
};
